/** Exact Figma field / button definitions – hospital vs authority (HMO) */

import { filterHeaderFieldsForOpVisit, isOpVisitType } from "./newPatientDetailsConstants";
import {
  IP_CLAIM_AMOUNT_FIELD_ROWS,
  IP_CLAIM_DOCUMENTS,
  OP_CLAIM_AMOUNT_FIELDS_AUTHORITY,
  OP_CLAIM_AMOUNT_FIELDS_HOSPITAL,
  OP_CLAIM_DOCUMENTS,
  OP_CLAIM_DOCUMENTS_AUTHORITY,
} from "./claimFieldUtils";

export type PatientDetailsSide = "hospital" | "authority";

export const FIGMA_DECLARATION_TEXT =
  "I hereby declare that the information furnished is true and correct to the best of my knowledge under CGHS scheme guidelines.";

export const HOSPITAL_QUERY_STAGE_LABELS = [
  "EMERGENCY",
  "REFERRAL ACCEPTANCE",
  "ENHANCEMENT",
  "DISCHARGE",
  "CLAIM",
  "SETTLEMENT",
] as const;

export const PROCEDURE_FIELDS = [
  { key: "procedure", label: "Procedure", type: "select" as const, placeholder: "Select" },
  { key: "procedureCode", label: "Procedure Code", type: "text" as const, placeholder: "Enter" },
  { key: "quantity", label: "Quantity/No of Days", type: "text" as const, placeholder: "Enter" },
  {
    key: "accreditationStatus",
    label: "Accreditation Status",
    type: "select" as const,
    placeholder: "Select",
  },
  {
    key: "treatmentStartDate",
    label: "Treatment Start Date",
    type: "datetime" as const,
    placeholder: "DD-MM-YYYY HH:mm:ss",
  },
  { key: "procedureAmount", label: "Procedure Amount", type: "text" as const, placeholder: "Amount" },
];

export const getFigmaConfig = (
  side: PatientDetailsSide,
  visitType?: string | null,
) => {
  const isHospital = side === "hospital";
  const isIpVisit = !isOpVisitType(visitType);
  const opAmountFields = isHospital
    ? OP_CLAIM_AMOUNT_FIELDS_HOSPITAL
    : OP_CLAIM_AMOUNT_FIELDS_AUTHORITY;
  const opDocuments = isHospital ? OP_CLAIM_DOCUMENTS : OP_CLAIM_DOCUMENTS_AUTHORITY;

  const hospitalHeaderFields = [
    "patientName",
    "marNo",
    "ward",
    "hospital",
    "package",
    "visitType",
    "doa",
    "dod",
    "claimId",
    "status",
    "lastUpdated",
  ] as const;
  const authorityHeaderFields = [
    "patientName",
    "marNo",
    "ward",
    "hospital",
    "package",
    "visitType",
    "doa",
    "dod",
    "claimId",
    "caseId",
    "status",
    "lastUpdated",
  ] as const;

  return {
    side,
    enhancements: {
      label: isHospital ? "ENHANCEMENT" : "View Enhancements",
      showCreateButton: isHospital && isIpVisit,
      showViewButton: isIpVisit,
    },
    headerFields: filterHeaderFieldsForOpVisit(
      isHospital ? hospitalHeaderFields : authorityHeaderFields,
      visitType,
    ),
    referral: {
      items: ["Referral Uploaded", "Photo", "Geo Tag"] as const,
      showCompleted: true,
      showLiveUpdates: isIpVisit,
    },
    discharge: {
      documents: ["Discharge Summary", "Geo Tag Photo", "Feedback Form"] as const,
      dateFirst: !isHospital,
      showActions: isHospital && isIpVisit,
      actionLabels: ["Save Draft", "Submit"] as const,
    },
    claim: {
      procedure: {
        showAddButton: isHospital,
        fields: PROCEDURE_FIELDS,
        totalLabel: "TOTAL AMOUNT: ₹ {amount}/-",
      },
      claimDetails: {
        beneficiaryLabel: isHospital
          ? "Any amount collected from beneficiary"
          : "Any amount collecting from beneficiary",
        showIfYesAmount: isHospital,
        supportingDocumentLabel: "Supporting Document",
        amountFields: isIpVisit
          ? IP_CLAIM_AMOUNT_FIELD_ROWS.flat()
          : opAmountFields,
        amountFieldRows: isIpVisit ? IP_CLAIM_AMOUNT_FIELD_ROWS : undefined,
      },
      documents: {
        title: isHospital ? "Request Document Upload" : "Claim Documents",
        mode: isHospital ? ("upload" as const) : ("download" as const),
        items: isIpVisit ? IP_CLAIM_DOCUMENTS : opDocuments,
      },
      showDeclaration: isHospital,
      actionLabels: isHospital ? (["Save Draft", "Submit"] as const) : ([] as const),
    },
    settlement: {
      fields: isHospital
        ? ([
            "Intimated Amount",
            "Approved Amount",
            "Gross Settled Amount",
            "Settlement ID",
            "TDS",
            "TAX",
            "Settled Date",
          ] as const)
        : ([
            "Net Settled Amount",
            "Approved Amount",
            "Gross Settled Amount",
            "Settlement ID",
            "TDS",
            "TAX",
            "Settled Date",
          ] as const),
      showApproveButton: !isHospital,
      approveLabel: "Approve",
    },
    billAudit: {
      hospitalBillLabel: "Hospital Bill",
      auditButtonLabel: "Bill audit",
      statusLabel: "Pending",
    },
    workflow: {
      amountLabel: "Amount",
      remarksLabel: "Remarks",
      actionLabels: ["APPROVE", "REJECT", "NMI"] as const,
      nmiDocumentLabel: "Additional Document",
      cmoFooterLabels: [ "Approve", "Reject"] as const,  //"Expert View",
    },
  };
};

export type FigmaConfig = ReturnType<typeof getFigmaConfig>;
